import { Link } from 'react-router-dom'
import { ModalShell } from '../../admin/modals/ModalShell'
import { useStaleItemDetail } from './useDrilldown'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

export function StaleItemActionModal({ itemId, onClose }: { itemId: string; onClose: () => void }) {
  const { data, isLoading, isError } = useStaleItemDetail(itemId)

  return (
    <ModalShell
      title={data ? `Хвост: ${data.partNumber}` : 'Позиция без движения'}
      subtitle={
        data
          ? `${data.daysInactive} дней без движения · заявка ${data.requestNumber ?? '—'}`
          : undefined
      }
      onClose={onClose}
      size="lg"
    >
      {isLoading && <p className="text-sm text-gray-400">Загрузка…</p>}
      {isError && <p className="text-sm text-red-600">Не удалось загрузить данные</p>}
      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Stat label="Количество" value={`${data.quantity} шт`} />
            <Stat label="Создана" value={formatDate(data.createdAt)} />
            <Stat label="Обновлена" value={formatDate(data.updatedAt)} />
          </div>

          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Действия</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <Link
                to={`/request-items/${data.itemId}`}
                onClick={onClose}
                className="rounded-xl border border-gray-200 bg-white p-3 text-sm hover:bg-gray-50"
              >
                <div className="font-medium text-gray-900">🔍 Открыть позицию</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Добавить квоту, сменить статус, посмотреть квоты
                </div>
              </Link>
              {data.manager && (
                <a
                  href={`mailto:${data.manager.email}?subject=Заявка ${data.requestNumber ?? ''} · ${data.partNumber}`}
                  className="rounded-xl border border-gray-200 bg-white p-3 text-sm hover:bg-gray-50"
                >
                  <div className="font-medium text-gray-900">✉ Сообщить менеджеру</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {data.manager.name} · {data.manager.email}
                  </div>
                </a>
              )}
              <Link
                to={`/requests/${data.requestId}`}
                onClick={onClose}
                className="rounded-xl border border-gray-200 bg-white p-3 text-sm hover:bg-gray-50"
              >
                <div className="font-medium text-gray-900">📋 Перейти в заявку</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Контекст, комментарии, другие позиции
                </div>
              </Link>
            </div>
          </div>

          {data.existingQuotes.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                Существующие квоты ({data.existingQuotes.length})
              </div>
              <div className="divide-y divide-gray-100 rounded-xl border border-gray-100">
                {data.existingQuotes.map((q, idx) => (
                  <div
                    key={`${q.supplierName}-${idx}`}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm"
                  >
                    <span className="flex-1 truncate text-gray-800">{q.supplierName}</span>
                    <span className="text-xs text-gray-700 tabular-nums w-24 text-right">
                      {q.price.toFixed(2)} {q.currency}
                    </span>
                    <span className="text-xs text-gray-500 tabular-nums w-16 text-right">
                      {formatDate(q.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </ModalShell>
  )
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-900 mt-0.5">{value}</div>
    </div>
  )
}
